import React from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@material-ui/core';
import {
  Edit,
  PauseCircleOutline,
  PlayCircleOutline,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import CustomTable from '../../components/CustomTable';
import CellaTesto from './CellaTesto';
import Pillola from './Pillola';
import StatoSondaBadge, { AiutoStatoSonda } from './StatoSondaBadge';
import {
  TITOLO_MODIFICA,
  TITOLO_RIATTIVA,
  TITOLO_SOSPENDI,
  descriviModalita,
  isSospesa,
} from './assegnazioniIotUtils';

const useStyles = makeStyles((theme) => ({
  cella: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: theme.spacing(1),
  },
  sonda: {
    fontFamily: 'monospace',
  },
}));

const AssegnazioniIotTable = ({
  assegnazioni,
  loading,
  onModifica,
  onRiattiva,
  onSospendi,
}) => {
  const classes = useStyles();

  const columns = [
    {
      Header: 'Sonda',
      id: 'sonda',
      minWidth: 160,
      accessor: (assegnazione) => assegnazione.sn,
      Cell: ({ original }) => (
        <CellaTesto testo={original.sn} className={classes.sonda} />
      ),
    },
    {
      Header: 'Attrezzatura',
      id: 'attrezzatura',
      minWidth: 160,
      accessor: (assegnazione) => assegnazione.attrezzaturaNome,
      Cell: ({ original }) => <CellaTesto testo={original.attrezzaturaNome} />,
    },
    {
      Header: 'Reparto',
      id: 'reparto',
      minWidth: 130,
      accessor: (assegnazione) => assegnazione.repartoNome,
      Cell: ({ original }) => <CellaTesto testo={original.repartoNome} />,
    },
    {
      Header: 'Controllo',
      id: 'controllo',
      minWidth: 200,
      accessor: (assegnazione) => assegnazione.controlloNome,
      Cell: ({ original }) => <CellaTesto testo={original.controlloNome} />,
    },
    {
      Header: 'Modalità di rilevazione',
      id: 'modalita',
      width: 170,
      accessor: (assegnazione) => assegnazione.modalita,
      Cell: ({ original }) => (
        <CellaTesto testo={descriviModalita(original.modalita)} />
      ),
    },
    {
      Header: () => <AiutoStatoSonda etichetta="Trasmissione" />,
      id: 'trasmissione',
      minWidth: 170,
      accessor: (assegnazione) => assegnazione.statoSonda ?? '',
      Cell: ({ original }) => <StatoSondaBadge assegnazione={original} />,
    },
    {
      Header: 'Monitoraggio',
      id: 'azioni',
      minWidth: 150,
      accessor: (assegnazione) => (isSospesa(assegnazione) ? 1 : 0),
      Cell: ({ original }) => {
        const sospesa = isSospesa(original);
        return (
          <div className={classes.cella}>
            <Pillola tono={sospesa ? 'neutral' : 'info'}>
              {sospesa ? 'Sospeso' : 'Attivo'}
            </Pillola>
            <Tooltip title={sospesa ? TITOLO_RIATTIVA : TITOLO_SOSPENDI} arrow>
              <IconButton
                size="small"
                color="primary"
                aria-label={sospesa ? TITOLO_RIATTIVA : TITOLO_SOSPENDI}
                onClick={() =>
                  sospesa ? onRiattiva(original) : onSospendi(original)
                }
              >
                {sospesa ? <PlayCircleOutline /> : <PauseCircleOutline />}
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
    {
      Header: '',
      id: 'modifica',
      width: 70,
      resizable: false,
      accessor: (assegnazione) => assegnazione,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          <Tooltip title={TITOLO_MODIFICA} arrow>
            <IconButton
              size="small"
              color="primary"
              aria-label={TITOLO_MODIFICA}
              onClick={() => onModifica(original)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <CustomTable
      records={assegnazioni}
      columns={columns}
      loading={loading}
      filterable={false}
      resizable={true}
      sortable={false}
      defaultPageSize={10}
    />
  );
};

AssegnazioniIotTable.propTypes = {
  assegnazioni: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onModifica: PropTypes.func.isRequired,
  onRiattiva: PropTypes.func.isRequired,
  onSospendi: PropTypes.func.isRequired,
};

export default AssegnazioniIotTable;
